import { ref, computed } from "vue";

// Theme engine: "system" follows the OS (prefers-color-scheme), "light"/"dark"
// force a mode. State lives on <body class="dark"> (the app.css dark block
// keys off it). Kept as module-level singletons so every consumer shares one
// source of truth.
//
//   initTheme()              once at startup (app.js) — reads saved mode,
//                            wires the system listener, applies immediately
//   themeMode                ref: "system" | "light" | "dark" (persisted)
//   isDark                   computed: effective dark state
//   setThemeMode(mode)       switch + persist + apply
//   applyTheme()             re-apply (also called again on f7ready so the
//                            native status bar stays in sync after F7's own
//                            statusbar init)

const STORAGE_KEY = "avocash.theme";
const MODES = ["system", "light", "dark"];

const THEME_COLOR = { light: "#1a6b42", dark: "#000000" };
const NATIVE_BAR = { light: "#F7FAF4", dark: "#161D18" };

const themeMode = ref("system");
const systemDark = ref(false);
const isDark = computed(() =>
  themeMode.value === "system" ? systemDark.value : themeMode.value === "dark"
);

let initialized = false;
let mql = null;

function syncThemeColorMeta(dark) {
  const metas = document.querySelectorAll('meta[name="theme-color"]');
  metas.forEach((meta) => {
    if (themeMode.value === "system") {
      // restore the media-query pair that ships in index.html
      const originalMedia = meta.getAttribute("data-original-media");
      if (originalMedia !== null) {
        meta.setAttribute("media", originalMedia);
        meta.removeAttribute("data-original-media");
      }
      const originalContent = meta.getAttribute("data-original-content");
      if (originalContent !== null) {
        meta.setAttribute("content", originalContent);
        meta.removeAttribute("data-original-content");
      }
    } else {
      if (meta.hasAttribute("media") && !meta.hasAttribute("data-original-media")) {
        meta.setAttribute("data-original-media", meta.getAttribute("media"));
        meta.removeAttribute("media");
      }
      if (!meta.hasAttribute("data-original-content")) {
        meta.setAttribute("data-original-content", meta.getAttribute("content") || "");
      }
      meta.setAttribute("content", dark ? THEME_COLOR.dark : THEME_COLOR.light);
    }
  });
}

async function syncNativeStatusBar(dark) {
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (!Capacitor.isNativePlatform()) return;
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light });
    if (Capacitor.getPlatform() === "android") {
      await StatusBar.setBackgroundColor({
        color: dark ? NATIVE_BAR.dark : NATIVE_BAR.light,
      });
    }
  } catch (e) {
    /* non-native platform or plugin unavailable — CSS handles it */
  }
}

function applyTheme() {
  if (typeof document === "undefined") return;
  document.body.classList.toggle("dark", isDark.value);
  syncThemeColorMeta(isDark.value);
  syncNativeStatusBar(isDark.value);
}

function setThemeMode(mode) {
  if (!MODES.includes(mode)) return;
  themeMode.value = mode;
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch (e) {
    /* storage unavailable — session-only */
  }
  applyTheme();
}

function initTheme() {
  if (initialized) return;
  initialized = true;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (MODES.includes(saved)) themeMode.value = saved;
  } catch (e) {
    /* keep default */
  }
  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    mql = window.matchMedia("(prefers-color-scheme: dark)");
    systemDark.value = mql.matches;
    const onChange = (e) => {
      systemDark.value = e.matches;
      if (themeMode.value === "system") applyTheme();
    };
    if (typeof mql.addEventListener === "function") mql.addEventListener("change", onChange);
    else if (typeof mql.addListener === "function") mql.addListener(onChange);
  }
  applyTheme();
}

export { themeMode, isDark, initTheme, applyTheme, setThemeMode };
export default initTheme;
